import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import useprodDetail from "../../store/detailProduct";
import useFruitStore from "../../store/fruits";
import Loader from "../../components/loader";
import { FaArrowLeft, FaStar, FaMapMarkerAlt, FaStore, FaCheckCircle, FaMinus, FaPlus, FaShoppingCart, FaCreditCard } from "react-icons/fa";
import useAddCart from "../../store/cart/addProduct";
import { toast, ToastContainer } from "react-toastify";
import useCartDetail from "../../store/cart/getcartDetails";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../../components/Breadcrumb";
import { URL } from "../../lib/apis";
import { HiOutlineTruck, HiOutlineShieldCheck } from "react-icons/hi";
import { MdOutlineVerified } from "react-icons/md";
import { scrollToTop } from "../../lib/utils";

interface Producer {
  address: string;
  city: string;
  country: string;
  created_at: string;
  departement: string;
  email: string;
  is_active: boolean;
  is_online: boolean;
  is_verified: boolean;
  name: string;
  otp_code: string;
  otp_created_at: string;
  phone_number: string;
  postal_code: string;
  profile_image_url: string;
  region: string;
  reset_password_expires: string;
  reset_password_token: string;
  role: string;
  role_id: number;
  two_factor_enabled: boolean;
  updated_at: string;
  user_id: number;
}

const ProduitDetail = () => {
  const [count, setCount] = useState<number>(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showImage, setShowImage] = useState<string>();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const { addToCart } = useAddCart((state) => state);
  const [producer, setProducer] = useState<Producer>();
  const { fetchAllCat } = useCartDetail((state) => state);
  const { t, i18n } = useTranslation();
  const userId = localStorage.getItem("user");
  const { prodDetail, loadingDetail, fetchprodDetail } = useprodDetail();
  const { fetchAllFruit } = useFruitStore();
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  const increment = () => setCount(count + 1);
  const decrement = () => {
    if (count > prodDetail?.minimum_quantity) {
      setCount(count - 1);
    }
  };

  const productId = useParams().id;

  useEffect(() => {
    scrollToTop()
  }, [])

  useEffect(() => {
    if (productId) {
      fetchprodDetail(productId);
    }
    fetchAllFruit();
  }, [productId, fetchAllFruit]);

  useEffect(() => {
    setCount(prodDetail?.minimum_quantity);
    if (prodDetail) {
      const images = JSON.parse(prodDetail.images);
      setShowImage(images[0]);
      setSelectedImageIndex(0);
    }
  }, [prodDetail]);

  const request = async () => {
    setLoading(true);
    if (!userId) {
      toast.warning(t("veuillez_connexion"));
      setLoading(false);
      return;
    }

    if (prodDetail?.quantity < count) {
      toast.warning(t("error_stock_insuffisant"));
      setLoading(false);
      return;
    }

    try {
      await addToCart({
        product_id: Number(productId),
        quantity: count,
        user_id: parseInt(userId ?? ""),
      });
      await fetchAllCat();
      toast.success(t("produit_ajoute_au_panier"));
    } catch (error: any) {
      toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE);
    } finally {
      setLoading(false);
    }
  };

  const passer_commande = async () => {
    setLoading(true);
    if (!userId) {
      toast.warning(t("veuillez_connexion"));
      setLoading(false);
      return;
    }

    if (prodDetail?.quantity < count) {
      toast.warning(t("error_stock_insuffisant"));
      setLoading(false);
      return;
    }

    try {
      await addToCart({
        product_id: Number(productId),
        quantity: count,
        user_id: parseInt(userId ?? ""),
      });
      await fetchAllCat();
      navigate("/cart");
    } catch (error: any) {
      toast.error(i18n.language === "fr" ? error.response.data.message : error.response.data.messageE);
    } finally {
      setLoading(false);
    }
  };

  const updateProducer = async () => {
    if (prodDetail) {
      await fetch(`${URL}/users/${prodDetail.user_id}`)
        .then((response) => {
          if (!response.ok) {
            toast.error("producteur introuvable");
          }
          return response.json();
        })
        .then((data) => {
          setProducer(data.user);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  useEffect(() => {
    updateProducer();
  }, [prodDetail]);

  if (loadingDetail || !prodDetail) {
    return <Loader />;
  }

  const productImages = prodDetail.images ? JSON.parse(prodDetail.images) : [];

  const handleImageClick = (image: string, index: number) => {
    setShowImage(image);
    setSelectedImageIndex(index);
  };

  const nextImage = () => {
    const nextIndex = (selectedImageIndex + 1) % productImages.length;
    setShowImage(productImages[nextIndex]);
    setSelectedImageIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = (selectedImageIndex - 1 + productImages.length) % productImages.length;
    setShowImage(productImages[prevIndex]);
    setSelectedImageIndex(prevIndex);
  };

  // Calcul du prix total
  const totalPrice = prodDetail.price * count;

  return (
    <div className="min-h-screen bg-[#f6f8f4]">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-primary/10 bg-white/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">

              <Breadcrumb />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
          {/* Left Column - Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div
              className="group relative aspect-[4/3] cursor-zoom-in overflow-hidden rounded-3xl border-2 border-[#93C01F]/25 bg-white shadow-[0_12px_35px_rgba(31,41,55,0.08)]"
              onClick={() => setIsImageZoomed(!isImageZoomed)}
            >
              <img
                alt="Product main"
                src={showImage}
                className={`h-full w-full object-cover transition-transform duration-500 ${isImageZoomed ? 'scale-150' : 'scale-100'
                  }`}
              />

              {/* Navigation Arrows */}
              {productImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    aria-label="Image précédente"
                    className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-primary shadow-lg transition-all duration-300 hover:scale-110 hover:bg-white"
                  >
                    <FaArrowLeft className="text-gray-700" size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    aria-label="Image suivante"
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-primary shadow-lg transition-all duration-300 hover:scale-110 hover:bg-white"
                  >
                    <FaArrowLeft className="text-gray-700 rotate-180" size={16} />
                  </button>
                </>
              )}

              {/* Image Counter */}
              {productImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-gray-900/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                  {selectedImageIndex + 1} / {productImages.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {productImages.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {productImages.map((image: any, index: any) => (
                  <div
                    key={index}
                    onClick={() => handleImageClick(image, index)}
                    className={`relative aspect-[4/3] cursor-pointer overflow-hidden rounded-xl border border-gray-100 transition-all duration-300 ${selectedImageIndex === index
                        ? 'scale-95 shadow-lg ring-2 ring-[#93C01F] ring-offset-2'
                        : 'hover:scale-95 hover:shadow-md'
                      }`}
                  >
                    <img
                      alt={`Product ${index + 1}`}
                      src={image}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Product Details */}
          <div className="space-y-6">
            {/* Producer Info */}
            <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-[0_10px_28px_rgba(31,41,55,0.06)]">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14 ring-4 ring-primary/10">
                  <AvatarImage src={producer?.profile_image_url} alt={producer?.name} />
                  <AvatarFallback className="bg-primary text-xl font-bold text-white">
                    {producer?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{producer?.name}</h3>
                    {producer?.is_verified && (
                      <MdOutlineVerified className="text-[#93C01F]" size={18} />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <FaMapMarkerAlt size={12} />
                    <span>{producer?.address}, {producer?.city}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="flex items-center gap-1 rounded-full bg-[#93C01F]/15 px-2 py-1 text-xs font-semibold text-[#6c920d]">
                      <HiOutlineTruck size={14} />
                      {producer?.is_online ? t('en_ligne') : t('hors_ligne')}
                    </span>
                    <span className="flex items-center gap-1 rounded-full bg-third/10 px-2 py-1 text-xs text-gray-700">
                      <HiOutlineShieldCheck size={14} />
                      {t('producteur_verifie')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-[0_10px_28px_rgba(31,41,55,0.06)] sm:p-6">
              <div className="mb-5 flex items-start justify-between gap-4">
                <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">
                  {i18n.language === "fr" ? prodDetail.product_name : prodDetail.product_name_en}
                </h1>
                <FaStore className="mt-1 shrink-0 text-primary/40" size={20} />
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1 text-third">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                </div>
                <span className="text-sm text-gray-500">(24 {t('avis')})</span>
              </div>

              <p className="text-gray-600 leading-relaxed mb-6">
                {prodDetail.description}
              </p>

              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="text-3xl font-black tracking-tight text-primary">
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "XAF",
                    }).format(prodDetail.price)}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">/ {i18n.language === "fr" ? prodDetail.type : prodDetail.type_en}</span>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${prodDetail.quantity > 0
                      ? 'bg-[#93C01F]/15 text-[#6c920d]'
                      : 'bg-secondary/10 text-secondary'
                    }`}>
                    {prodDetail.quantity > 0 ? t('en_stock') : t('rupture_stock')}
                  </span>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-5 rounded-2xl border border-[#93C01F]/25 bg-[#93C01F]/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700">{t("quantite")}</span>
                    <div className="flex items-center gap-3 rounded-xl border border-[#93C01F]/25 bg-white p-1 shadow-sm">
                      <button
                        onClick={decrement}
                        disabled={count <= prodDetail.minimum_quantity}
                        className={`p-2 rounded-lg transition-all duration-200 ${count <= prodDetail.minimum_quantity
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-primary/10 hover:text-primary'
                          }`}
                      >
                        <FaMinus size={12} />
                      </button>
                      <span className="text-xl font-semibold text-gray-900 min-w-[40px] text-center">
                        {count}
                      </span>
                      <button
                        onClick={increment}
                        disabled={count >= prodDetail.quantity}
                        className={`p-2 rounded-lg transition-all duration-200 ${count >= prodDetail.quantity
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-primary/10 hover:text-primary'
                          }`}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">{t("quantite_min")}</span>
                    <p className="font-medium">{prodDetail.minimum_quantity} {i18n.language === "fr" ? prodDetail.type : prodDetail.type_en}</p>
                  </div>
                </div>
              </div>

              {/* Total Price */}
              <div className="mb-5 rounded-2xl border border-primary/15 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{t("total")}</span>
                  <span className="text-3xl font-dm font-bold text-primary">
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "XAF",
                    }).format(totalPrice)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={request}
                  disabled={loading}
                  className="flex-1 rounded-xl bg-[#93C01F] px-6 py-4 font-semibold text-white shadow-lg shadow-third/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-third/90 hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t("ajout_en_cours")}</span>
                    </>
                  ) : (
                    <>
                      <FaShoppingCart size={18} />
                      <span>{t("add_cart")}</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={passer_commande}
                  disabled={loading}
                  className="flex-1 rounded-xl bg-primary px-6 py-4 font-semibold text-white shadow-lg shadow-primary/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl"
                >
                  <FaCreditCard size={18} />
                  <span>{t("passer_commande")}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ProduitDetail;